import { toast } from 'react-hot-toast';
import { setModal } from '../Store/Actions/modalActions';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useGlobals from './useGlobals';
import { useSelector } from 'react-redux';
import { SocketContext } from '../SocketContext';
import { RootState } from '../Store/Reducers';
import { ChatList, ChatListGroup, ModalData } from '../Types';
import { ChatDeleteMessage, SocketMessageTypes, TitleListUpdateMessage } from '../Types/SocketMessage';
import { setChatlistClear, setCombinedPrompts, setSelectedPrompt } from '../Store/Actions/environmentActions';
import { chatService } from '../Services/chatService';
import { setNavigationSubTitle, setNavigationTitle } from '../Store/Actions/navigationHeaderActions';

const useChatList = () => {
    const { handleNavigate, currentURL, i18n, dispatch, isCtrlOrCmdPressed, selectedTheme, currentChatId } =
        useGlobals();

    const { addMessageHandler } = useContext(SocketContext);
    const chatList = useSelector((state: RootState) => state.chatlist.chatlist as ChatList[]);
    const chatlistOpen = useSelector((state: RootState) => state.chatlist.chatlistOpen as boolean);
    const chatlistOpenHover = useSelector((state: RootState) => state.chatlist.chatlistOpenHover as boolean);
    const [ListItemEdit, setListItemEdit] = useState<string>('');
    const [filterValue, setFilter] = useState<string>('');
    const [chatsFetched, setChatsFetched] = useState<boolean>(false);
    const [allChatsLoaded, setAllChatsLoaded] = useState<ChatList[]>([]);
    const basePrompt = useSelector((state: RootState) => state.environment.basePrompt);
    const themes = useSelector((state: any) => state.themes.allThemes);
    const userPrompts = useSelector((state: RootState) => state.environment.userPrompts);
    const themeLogo = themes[selectedTheme]?.logo ?? '';
    const clearChatlist = useSelector((state: RootState) => state.environment.chatlistClear);

    const fetchAllChats = useCallback(async () => {
        try {
            setChatsFetched(false);
            let allChats: ChatList[] = [];
            let currentPage = 1;
            let hasMorePages = true;
            const itemsPerPage = 50;

            while (hasMorePages) {
                const response = await chatService.getChatListByPage(currentPage, itemsPerPage);
                const pageData = response.data.data;

                allChats = [...allChats, ...pageData];

                hasMorePages = pageData.length === itemsPerPage;
                currentPage++;
            }

            setAllChatsLoaded(allChats);
            setChatsFetched(true);
        } catch (error) {
            console.error('Error fetching chats:', error);
            setChatsFetched(true);
        }
    }, []);

    const handleChatUpdated = useCallback((data: any) => {
        const chatIdentifier = data.chatIdentifier || data.identifier;
        if (!chatIdentifier) return;
        setAllChatsLoaded((prev) => {
            const next = prev.map((chat) => {
                if (chat.identifier === chatIdentifier) {
                    return {
                        ...chat,
                        updatedAt: data.updatedAt,
                        title: data.title ?? chat.title,
                    };
                }
                return chat;
            });

            return next;
        });
    }, []);

    useEffect(() => {
        addMessageHandler(SocketMessageTypes.chatUpdated, handleChatUpdated);
    }, [addMessageHandler, handleChatUpdated]);

    const resetChats = useCallback(() => {
        setAllChatsLoaded([]);
        fetchAllChats();
    }, [fetchAllChats]);

    const filteredChats = useMemo(() => {
        if (!filterValue.trim()) {
            return allChatsLoaded;
        }

        return allChatsLoaded.filter(
            (chat) =>
                chat.title.toLowerCase().includes(filterValue.toLowerCase().trim()) ||
                chat.assistantTitle?.toLowerCase().includes(filterValue.toLowerCase().trim()),
        );
    }, [allChatsLoaded, filterValue]);

    const handleChatDeleted = useCallback(
        (data: ChatDeleteMessage) => {
            const updatedAllChats = allChatsLoaded.filter(
                (chatListItem: ChatList) => chatListItem.identifier !== data.chatIdentifier,
            );

            setAllChatsLoaded(updatedAllChats);

            if (data.chatIdentifier === currentChatId) {
                handleNavigate('/chats');
            }
        },
        [currentChatId, handleNavigate, allChatsLoaded],
    );

    const navigateToHome = useCallback(() => {
        dispatch(setSelectedPrompt(basePrompt?.identifier!));
        handleNavigate('/start');
    }, [basePrompt, handleNavigate, dispatch]);

    const deleteChat = useCallback(
        async (chatIdentifier: string) => {
            if (chatIdentifier === currentChatId) {
                dispatch(setNavigationTitle(''));
                dispatch(setNavigationSubTitle(''));
            }
            chatService.deleteChat(chatIdentifier).then(() => {
                handleChatDeleted({ chatIdentifier: chatIdentifier });

                toast.success(i18n.translate({ id: 'toast.chatDeleted', sourceName: 'ChatList' }));
            });
        },
        [handleChatDeleted, i18n, dispatch, currentChatId],
    );

    const deleteChatHandler = useCallback(
        (chatIdentifier: string) => {
            if (isCtrlOrCmdPressed) {
                deleteChat(chatIdentifier);
            } else {
                dispatch(
                    setModal({
                        state: 'open',
                        title: i18n.translate({ id: 'chatDelete.conversation', sourceName: 'Chat' }),
                        content: i18n.translate({ id: 'chat.conversation', sourceName: 'Chat' }),
                        buttonRightText: i18n.translate({ id: 'confirmDeleteChat.button', sourceName: 'Main' }),
                        buttonRightAction: () => {
                            if (chatIdentifier) {
                                deleteChat(chatIdentifier);
                            }
                        },
                        buttonLeftText: i18n.translate({ id: 'closeConfirmDialog.button', sourceName: 'Main' }),
                        buttonLeftAction: () => {
                            dispatch(setModal({ state: 'closed' }));
                        },
                        closeOnAction: true,
                        showCloseButton: false,
                    } as ModalData),
                );
            }
        },
        [deleteChat, dispatch, i18n, isCtrlOrCmdPressed],
    );

    const handleTitleListUpdate = useCallback(
        (data: TitleListUpdateMessage) => {
            const updatedAllChats = allChatsLoaded.map((chatlistItem: ChatList) =>
                chatlistItem.identifier === data.chatIdentifier ? { ...chatlistItem, title: data.title } : chatlistItem,
            );

            setAllChatsLoaded(updatedAllChats);
        },
        [allChatsLoaded],
    );

    const updateTitle = useCallback(
        async (chatIdentifier: string, chatTitle: string) => {
            chatService.updateChat(chatIdentifier, chatTitle).then(() => {
                setListItemEdit('');
                handleNavigate(`/chats/${chatIdentifier}`);
                handleTitleListUpdate({ chatIdentifier: chatIdentifier, title: chatTitle });
            });
        },
        [handleNavigate, setListItemEdit, handleTitleListUpdate],
    );

    const headlines = useMemo(
        () => ({
            today: i18n.translate({ id: 'sort.today', sourceName: 'ChatList' }),
            yesterday: i18n.translate({ id: 'sort.yesterday', sourceName: 'ChatList' }),
            week: i18n.translate({ id: 'sort.week', sourceName: 'ChatList' }),
            month: i18n.translate({ id: 'sort.month', sourceName: 'ChatList' }),
            older: i18n.translate({ id: 'sort.older', sourceName: 'ChatList' }),
        }),
        [i18n],
    );

    const rangeToHeadline = useMemo(
        () => [
            { maxDays: 0, headline: headlines.today },
            { maxDays: 1, headline: headlines.yesterday },
            { maxDays: 7, headline: headlines.week },
            { maxDays: 30, headline: headlines.month },
        ],
        [headlines],
    );

    const groupChatsByHeadline = useCallback(
        (chatlist: ChatList[]) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const sortedChatList = [...chatlist].sort((a, b) => {
                const dateA = new Date(a.updatedAt).getTime();
                const dateB = new Date(b.updatedAt).getTime();
                return dateB - dateA;
            });

            const chatListWithHeadlines = sortedChatList.map((chatlistItem) => {
                const chatDate = new Date(chatlistItem.updatedAt);
                chatDate.setHours(0, 0, 0, 0);

                const diffInDays = Math.floor((today.getTime() - chatDate.getTime()) / (1000 * 60 * 60 * 24));

                const headline =
                    rangeToHeadline.find((range) => diffInDays <= range.maxDays)?.headline || headlines.older;

                return { ...chatlistItem, headline };
            });

            const grouped = chatListWithHeadlines.reduce(
                (
                    groupedChatlistItems: {
                        [key: string]: ChatList[];
                    },
                    chatlistItem: ChatList & ChatListGroup,
                ) => {
                    (groupedChatlistItems[chatlistItem.headline] =
                        groupedChatlistItems[chatlistItem.headline] || []).push(chatlistItem);
                    return groupedChatlistItems;
                },
                {},
            );

            Object.keys(grouped).forEach((key) => {
                grouped[key].sort((a, b) => {
                    const dateA = new Date(a.updatedAt).getTime();
                    const dateB = new Date(b.updatedAt).getTime();
                    return dateB - dateA;
                });
            });

            return grouped;
        },
        [rangeToHeadline, headlines],
    );

    const groupedChats = useMemo(() => groupChatsByHeadline(filteredChats), [groupChatsByHeadline, filteredChats]);

    useEffect(() => {
        chatService.getBaseAndLastUpdatedPrompt().then((response: any) => {
            dispatch(setCombinedPrompts(response.data));
        });
    }, [dispatch, userPrompts, chatList]);

    useEffect(() => {
        fetchAllChats();
    }, [fetchAllChats]);

    useEffect(() => {
        if (clearChatlist) {
            setAllChatsLoaded([]);
            fetchAllChats();
            dispatch(setChatlistClear(false));
        }
    }, [fetchAllChats, clearChatlist, dispatch]);

    useEffect(() => {
        addMessageHandler(SocketMessageTypes.chatDeleted, handleChatDeleted);
        addMessageHandler(SocketMessageTypes.titleListUpdate, handleTitleListUpdate);
        addMessageHandler(SocketMessageTypes.chatCreated, resetChats);
    }, [addMessageHandler, resetChats, handleChatDeleted, handleTitleListUpdate]);

    const ChatListActions = useMemo(
        () => ({
            deleteChat,
            deleteChatHandler,
            updateTitle,
            fetchAllChats,
            groupChatsByHeadline,
            navigateToHome,
            resetChats,
        }),
        [deleteChat, deleteChatHandler, updateTitle, fetchAllChats, groupChatsByHeadline, navigateToHome, resetChats],
    );

    return {
        ChatListActions,
        i18n,
        chatlistOpen,
        chatlistOpenHover,
        ListItemEdit,
        setListItemEdit,
        filterValue,
        setFilter,
        themeLogo,
        chatsFetched,
        groupedChats,
        currentURL,
        dispatch,
        allChatsLoaded: filteredChats,
    };
};

export default useChatList;
